import { useEffect, useState } from 'react';
import { Button, Checkbox, Group, NumberInput, Stack, TextInput } from '@mantine/core';
import type {
  CargoTemplateCreateInput,
  CargoTemplateDto,
  CargoTemplatePatchInput,
} from '@expeditor/shared';
import { cargoTemplatesApi } from '../../shared/api/reference-books';
import { ReferenceBookTable } from './ReferenceBookTable';

export function CargoTemplatesPage() {
  return (
    <ReferenceBookTable<CargoTemplateDto, CargoTemplateCreateInput, CargoTemplatePatchInput>
      title="Мои грузы"
      api={cargoTemplatesApi}
      columns={[
        { header: 'Наименование', render: (i) => i.label },
        { header: 'Упаковка', render: (i) => i.packaging ?? '—' },
        { header: 'Вес ед., кг', render: (i) => i.defaultWeightKg ?? '—' },
        { header: 'Объём ед., м³', render: (i) => i.defaultVolumeM3 ?? '—' },
        { header: 'Опасный', render: (i) => (i.isDangerous ? `${i.adrClass ?? '?'}` : '—') },
      ]}
      renderForm={CargoForm}
    />
  );
}

function CargoForm({
  initial,
  onSubmit,
  submitting,
}: {
  initial: CargoTemplateDto | null;
  onSubmit: (dto: CargoTemplateCreateInput | CargoTemplatePatchInput) => Promise<void>;
  submitting: boolean;
}) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [packaging, setPackaging] = useState(initial?.packaging ?? '');
  const [weight, setWeight] = useState<number | string>(initial?.defaultWeightKg ?? '');
  const [volume, setVolume] = useState<number | string>(initial?.defaultVolumeM3 ?? '');
  const [tnVedCode, setTnVedCode] = useState(initial?.tnVedCode ?? '');
  const [gngCode, setGngCode] = useState(initial?.gngCode ?? '');
  const [etSngCode, setEtSngCode] = useState(initial?.etSngCode ?? '');
  const [isDangerous, setIsDangerous] = useState(initial?.isDangerous ?? false);
  const [adrClass, setAdrClass] = useState(initial?.adrClass ?? '');

  useEffect(() => {
    setLabel(initial?.label ?? '');
    setName(initial?.name ?? '');
    setPackaging(initial?.packaging ?? '');
    setWeight(initial?.defaultWeightKg ?? '');
    setVolume(initial?.defaultVolumeM3 ?? '');
    setTnVedCode(initial?.tnVedCode ?? '');
    setGngCode(initial?.gngCode ?? '');
    setEtSngCode(initial?.etSngCode ?? '');
    setIsDangerous(initial?.isDangerous ?? false);
    setAdrClass(initial?.adrClass ?? '');
  }, [initial?.id]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSubmit({
      label,
      name,
      // category — скрыта в UI; не передаём, бэк сохранит null.
      packaging: packaging || undefined,
      defaultWeightKg: weight === '' ? undefined : Number(weight),
      defaultVolumeM3: volume === '' ? undefined : Number(volume),
      tnVedCode: tnVedCode || undefined,
      gngCode: gngCode || undefined,
      etSngCode: etSngCode || undefined,
      isDangerous,
      adrClass: isDangerous ? adrClass || undefined : undefined,
    });
  };

  return (
    <form onSubmit={submit}>
      <Stack>
        <TextInput
          label="Наименование груза (для себя)"
          description="Короткое имя для быстрого поиска в списке"
          required
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
        />
        <TextInput
          label="Описание груза (для экспедитора и документов)"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Group grow>
          <NumberInput
            label="Вес единицы товара, кг"
            value={weight}
            onChange={setWeight}
            min={0}
            decimalScale={2}
            step={0.01}
          />
          <NumberInput
            label="Объём единицы товара, м³"
            value={volume}
            onChange={setVolume}
            min={0}
            decimalScale={2}
            step={0.01}
          />
        </Group>
        <TextInput
          label="Упаковка"
          value={packaging}
          onChange={(e) => setPackaging(e.currentTarget.value)}
        />
        <Group grow>
          <TextInput
            label="Код ТН ВЭД"
            value={tnVedCode}
            onChange={(e) => setTnVedCode(e.currentTarget.value)}
          />
          <TextInput
            label="Код ГНГ"
            value={gngCode}
            onChange={(e) => setGngCode(e.currentTarget.value)}
          />
          <TextInput
            label="Код ЕТСНГ"
            value={etSngCode}
            onChange={(e) => setEtSngCode(e.currentTarget.value)}
          />
        </Group>
        <Checkbox
          label="Опасный груз"
          checked={isDangerous}
          onChange={(e) => setIsDangerous(e.currentTarget.checked)}
        />
        {isDangerous && (
          <TextInput
            label="Класс опасности по ДОПОГ"
            required
            value={adrClass}
            onChange={(e) => setAdrClass(e.currentTarget.value)}
          />
        )}
        <Button type="submit" loading={submitting}>
          Сохранить
        </Button>
      </Stack>
    </form>
  );
}
